import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { notifyError } from '../../utils/notifications';
import { NcReviewVesselListCollapsedTable } from './NcReviewVesselListCollapsedTable';
import { NcReviewVesselListFilters } from './NcReviewVesselListFilters';
import { NcReviewVesselListTable, buildNcReviewUrl } from './NcReviewVesselListTable';
const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const allLevels = [0, 1, 2, 3, 4];
const parseOptionalInt = (searchParams, key) => {
    const value = searchParams.get(key);
    if (value === null) {
        return null;
    }
    const parsedValue = Number.parseInt(value, 10);
    return Number.isNaN(parsedValue) ? null : parsedValue;
};
const buildInitialFilters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);
    const selectedLevels = (searchParams.get('selectedLevels') ?? '')
        .split(',')
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => !Number.isNaN(value));
    return {
        selectedMonthFrom: parseOptionalInt(searchParams, 'selectedMonthFrom') ?? lastMonth.getMonth(),
        selectedMonthTo: parseOptionalInt(searchParams, 'selectedMonthTo') ?? today.getMonth(),
        selectedYearFrom: parseOptionalInt(searchParams, 'selectedYearFrom') ?? lastMonth.getFullYear(),
        selectedYearTo: parseOptionalInt(searchParams, 'selectedYearTo') ?? today.getFullYear(),
        selectedFleetId: parseOptionalInt(searchParams, 'selectedFleetId'),
        selectedLevels: selectedLevels.length > 0 ? selectedLevels : allLevels,
    };
};
const buildYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, index) => currentYear - index);
};
export const NcReviewVesselListPage = () => {
    const [filters, setFilters] = useState(buildInitialFilters);
    const [fleets, setFleets] = useState([]);
    const [vessels, setVessels] = useState([]);
    const [loading, setLoading] = useState(true);
    const years = useMemo(() => buildYears(), []);
    useEffect(() => {
        const loadFleets = async () => {
            try {
                const response = await fetch('/api/reporting/getfleets');
                if (!response.ok) {
                    throw new Error('Could not load fleets.');
                }
                const fleetResults = await response.json();
                setFleets(fleetResults);
                setFilters((currentFilters) => {
                    const hasSelectedFleet = currentFilters.selectedFleetId !== null
                        && fleetResults.some((fleet) => fleet.ID === currentFilters.selectedFleetId);
                    const nextFleetId = hasSelectedFleet ? currentFilters.selectedFleetId : (fleetResults[0]?.ID ?? null);
                    return nextFleetId === currentFilters.selectedFleetId
                        ? currentFilters
                        : { ...currentFilters, selectedFleetId: nextFleetId };
                });
            }
            catch (error) {
                notifyError(error instanceof Error ? error.message : 'Could not load fleets.');
                setLoading(false);
            }
        };
        void loadFleets();
    }, []);
    useEffect(() => {
        const searchParams = new URLSearchParams({
            selectedMonthFrom: String(filters.selectedMonthFrom),
            selectedMonthTo: String(filters.selectedMonthTo),
            selectedYearFrom: String(filters.selectedYearFrom),
            selectedYearTo: String(filters.selectedYearTo),
            selectedLevels: filters.selectedLevels.join(','),
        });
        if (filters.selectedFleetId !== null) {
            searchParams.set('selectedFleetId', String(filters.selectedFleetId));
        }
        window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
    }, [filters]);
    useEffect(() => {
        if (filters.selectedFleetId === null) {
            setVessels([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        let isCancelled = false;
        const timeoutId = window.setTimeout(() => {
            void (async () => {
                try {
                    const response = await fetch(`/api/ncreview/vessels?${new URLSearchParams({
                        monthFrom: String(filters.selectedMonthFrom + 1),
                        yearFrom: String(filters.selectedYearFrom),
                        monthTo: String(filters.selectedMonthTo + 1),
                        yearTo: String(filters.selectedYearTo),
                        fleetId: String(filters.selectedFleetId),
                        levels: filters.selectedLevels.join(','),
                    }).toString()}`);
                    if (response.status === 400) {
                        throw new Error(await response.text());
                    }
                    if (!response.ok) {
                        throw new Error('An error has occured');
                    }
                    const vesselResults = await response.json();
                    if (!isCancelled) {
                        setVessels(vesselResults);
                    }
                }
                catch (error) {
                    if (!isCancelled) {
                        notifyError(error instanceof Error ? error.message : 'An error has occured');
                        setVessels([]);
                    }
                }
                finally {
                    if (!isCancelled) {
                        setLoading(false);
                    }
                }
            })();
        }, 250);
        return () => {
            isCancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [filters]);
    const vesselsToReview = useMemo(() => vessels.filter((vessel) => vessel.NumberOfNcsToReview > 0), [vessels]);
    const vesselsWithoutNcs = useMemo(() => vessels.filter((vessel) => vessel.NumberOfNcsToReview === 0), [vessels]);
    const openVesselReview = (vessel, showAcknowledged) => {
        window.location.href = buildNcReviewUrl(vessel, showAcknowledged, window.location.search);
    };
    return (_jsx("div", { className: "container", children: _jsxs("div", { className: "card shadow", children: [_jsxs("div", { className: "card-header position-relative", children: [_jsx("h2", { children: "Non-conformities review & acknowledgement" }), _jsx("h3", { children: "Manual review and acceptance of vessel non-conformities, as per SIRE 2.0 regulations." })] }), _jsxs("div", { className: "card-body", children: [_jsx(NcReviewVesselListFilters, { fleets: fleets, filters: filters, monthNames: monthNames, years: years, onChange: setFilters }), _jsx(NcReviewVesselListTable, { rows: vesselsToReview, title: "Vessels with NCs to review", tableId: "ncToReviewTable", loading: loading, onOpenVessel: (vessel) => openVesselReview(vessel, false) }), _jsx(NcReviewVesselListCollapsedTable, { rows: vesselsWithoutNcs, onOpenVessel: (vessel) => openVesselReview(vessel, true), loading: loading })] })] }) }));
};
